import { useSaveVaxFormDataMutation } from 'api/vax.api.js';
import useFormUpdate from 'components/impfschaeden/assistent/hooks/useFormUpdate.js';
import useLocalStorage from 'components/impfschaeden/assistent/hooks/useLocalStorage.js';
import {
	AssistantCard,
	Button,
	CheckBox,
	Col,
	Container,
	FormText,
	LoadingButton,
	ProgressBar,
	Row,
	Stack,
} from 'components/ui';
import useUpdateCheck from 'hooks/useUpdateCheck.js';
import { useTranslation } from 'next-i18next';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import postError from 'utils/postError';
import { formActions } from '../../../store/vaxFormDataSlice';
import useValidation from './hooks/useValidation';
import Step01 from './VaxForm_01_Begruessung/VaxForm_01';
import Step02 from './VaxForm_02_Benutzerinformation/VaxForm_02';
import Step03 from './VaxForm_03_AnderePerson/VaxForm_03';
import Step04 from './VaxForm_04_InfoZurPerson/VaxForm_04';
import Step05a from './VaxForm_05a_VerbindungZurPerson/VaxForm_05a';
import Step06a from './VaxForm_06a_WieErfahren/VaxForm_06a';
import Step07 from './VaxForm_07_Vorerkrankungen/VaxForm_07.js';
import Step08 from './VaxForm_08_Impfschaeden/VaxForm_08';
import Step09 from './VaxForm_09_Impfungen/VaxForm_09';
import Step10 from './VaxForm_10_Profilbild/VaxForm_10';
import Step11 from './VaxForm_11_BilderVideos/VaxForm_11';
import Step12 from './VaxForm_12_Bestaetigen/VaxForm_12';
import Step13 from './VaxForm_13_Abschluss/VaxForm_13';

const Headline = styled.h1`
	color: #4195e1;
	font-weight: bold;
`;

const renderStep = (
	currentPage,
	updatePage,
	setProgress,
	calcProgress,
	loading
) => {
	const dispatch = useDispatch();
	const vaxFormData = useSelector((state) => state.vaxFormData);
	switch (currentPage) {
		case 1:
			return <Step01 />;
		case 2:
			return <Step02 />;
		case 3:
			return (
				<Step03
					nextToAnotherPers={() => {
						dispatch(formActions.update({ anotherPerson: true }));
						updatePage(4);
						setProgress(calcProgress(4));
					}}
					nextToSamePers={() => {
						dispatch(
							formActions.update({
								anotherPerson: false,
								first_name: vaxFormData.user?.first_name,
								last_name: vaxFormData.user?.last_name,
							})
						);
						updatePage(4);
						setProgress(calcProgress(4));
					}}
				/>
			);
		case 4:
			return <Step04 />;
		case 5:
			return <Step05a />;
		case 6:
			return <Step06a />;
		case 7:
			return <Step07 />;
		case 8:
			return <Step08 />;
		case 9:
			return <Step09 />;
		case 10:
			return <Step10 />;
		case 11:
			return <Step11 />;
		case 12:
			return <Step12 loading={loading} />;
		case 13:
			return <Step13 />;
		default:
			return (
				<>
					<div>invalid step</div>
				</>
			);
	}
};

const getInitialPage = (user) => {
	const { hasUpdate } = useUpdateCheck();
	if (typeof window !== 'undefined' && !hasUpdate()) {
		const storedPage = localStorage.getItem('currentPage');
		if (storedPage) {
			if ((storedPage == 1 || storedPage == 2) && user) {
				return 3;
			}
			if (parseInt(storedPage, 10) > 2 && !user) {
				return 2;
			}
			return parseInt(storedPage, 10);
		}
	}
	if (user) {
		return 3;
	}
	return 1;
};

const ImpfFormStart = () => {
	const pageCount = 11;

	const [t] = useTranslation(['translation']);
	const { validateForm, clearValidation, isValid } = useValidation();
	const user = useSelector((state) => state.settings.currentUser);
	const language = useSelector((state) => state.settings.language);
	const [currentPage, setPage] = useState(getInitialPage(user));
	const [keepData, setKeepData] = useState(false);
	const [progress, setProgress] = useState(calcProgress(getInitialPage()));
	const [loading, setLoading] = useState(false);
	const [saveVaxFormData, { error }] = useSaveVaxFormDataMutation();
	const [showErrorDetail, setShowErrorDetail] = useState(false);
	const [showError, setShowError] = useState(false);
	const vaxFormData = useSelector((state) => state.vaxFormData);
	const [confirmed, setConfirmed] = useState(false);
	const files = useSelector((state) => state.file);
	const { updateUser } = useFormUpdate('vaxStep2');
	useLocalStorage();
	const dispatch = useDispatch();

	const toggleKeepData = () => {
		setKeepData(!keepData);
	};

	useEffect(() => {
		if (!user) {
			return;
		}
		updateUser({
			user_salutation_id: user.user_salutation_id,
			title: user.title,
			first_name: user.first_name,
			last_name: user.last_name,
			email: user.email,
		});
	}, [user]);

	useEffect(() => {
		if (!user && currentPage == 3) {
			updatePage(1);
		}
		if (user && (currentPage == 1 || currentPage == 2)) {
			updatePage(3);
		}
	}, [user]);

	const toggleError = () => {
		setShowErrorDetail(!showErrorDetail);
	};

	const updatePage = (page) => {
		setPage(page);
		if (typeof window !== 'undefined') {
			localStorage.setItem('currentPage', page);
		}
	};

	const hasNewVaccineTypeOrManufacturer = () => {
		let newVaxTypes = false;
		let newVaxManufacturers = false;
		for (let i = 0; i < vaxFormData.vax_entry_vaccinations.length; i++) {
			if (
				typeof vaxFormData.vax_entry_vaccinations[i].vax_type_id == 'string'
			) {
				newVaxTypes = true;
			}
			if (
				typeof vaxFormData.vax_entry_vaccinations[i].vax_manufacturer_id ==
				'string'
			) {
				newVaxManufacturers = true;
			}
		}
		if (newVaxTypes && newVaxManufacturers) {
			return 'both';
		} else if (newVaxTypes) {
			return 'newVaxTypes';
		} else if (newVaxManufacturers) {
			return 'newManufacturers';
		} else {
			return false;
		}
	};

	const saveVaxForm = async (publish = false) => {
		setLoading(true);
		let data = {
			...vaxFormData,
			lang_iso_code: language,
			files: files,
		};
		if (publish && user?.is_expert == 1) {
			data.glob_entry_status_id = 2;
		}
		const result = await saveVaxFormData(data)
			.unwrap()
			.catch((e) => {
				console.log('error saveVaxForm', e);
				postError('test');
			});
		if (!result) {
			return;
		}
		if (!keepData) {
			localStorage.removeItem('vaxFormData');
			dispatch(formActions.resetForm());
		}
		if (user?.is_expert == 1) {
			updatePage(3);
		} else {
			if (result?.confirmed == 1) {
				setConfirmed(true);
			}
			nextPage();
		}
		postError('success');
		setLoading(false);
	};

	function calcProgress(step) {
		return Math.round((100 / pageCount) * (step - 1));
	}

	const prevPage = () => {
		setShowErrorDetail(false);
		setShowError(false);
		clearValidation();
		let prevPage;
		if (currentPage === 7) {
			if (vaxFormData.anotherPerson === true) {
				prevPage = 6;
			} else if (vaxFormData.anotherPerson === false) {
				prevPage = 4;
			} else {
				prevPage = 3;
			}
		} else {
			prevPage = currentPage - 1;
		}
		updatePage(prevPage);
		setProgress(calcProgress(prevPage));
	};

	const firstPage = () => {
		if (!keepData) {
			dispatch(formActions.resetForm());
		}
		let nextPage = 1;
		if (user) {
			nextPage = 3;
		}
		updatePage(nextPage);
		setProgress(calcProgress(nextPage));
	};

	const nextPage = () => {
		let nextPage;
		if ((currentPage > 1 && currentPage < 10) || currentPage == 11) {
			const validationResult = validateForm(
				'vaxStep' + currentPage,
				vaxFormData
			);
			if (!isValid(validationResult)) {
				return;
			}
		}
		if (currentPage == 4) {
			if (vaxFormData.anotherPerson === true) {
				nextPage = 5;
			} else if (vaxFormData.anotherPerson === false) {
				nextPage = 7;
			} else {
				nextPage = 3;
			}
		} else {
			nextPage = currentPage + 1;
		}
		updatePage(nextPage);
		setProgress(calcProgress(nextPage));
	};

	useEffect(() => {
		setLoading(false);
		setShowError(!!error);
	}, [error]);

	return (
		<>
			<Row>
				<Col style={{ whiteSpace: 'nowrap' }}>
					<Headline>{t('impfschaeden.erfassen.impfschaden_erfassen')}</Headline>
				</Col>
				{currentPage != 1 && currentPage != 13 && (
					<Col className='my-auto d-flex flex-column align-items-end'>
						<div className='w-100'>
							<ProgressBar progress={progress} />
						</div>
						<div>
							{t('common.page')} {currentPage}
						</div>
					</Col>
				)}
			</Row>
			<AssistantCard className='card-body rounded-0 border border-light'>
				{renderStep(
					currentPage,
					updatePage,
					setProgress,
					calcProgress,
					loading,
					confirmed
				)}

				<Container>
					{showError && (
						<Row className='mt-3'>
							<Col className='alert alert-danger'>
								Es gab einen Fehler versuchen Sie es erneut.{' '}
								<Button onClick={toggleError} variant='danger'>
									Details
								</Button>
								{error && showErrorDetail && (
									<div>
										{error.status} {JSON.stringify(error.data)}
									</div>
								)}
							</Col>
						</Row>
					)}
					<Row className='mt-3'>
						{currentPage > 1 && currentPage < 10 && (
							<Col xs={4} style={{ justifyContent: 'flex-end' }}>
								<FormText className='text-muted'>
									* {t('common.pflichtfeld')}{' '}
								</FormText>
							</Col>
						)}
						<Col>
							{currentPage == 13 && (
								<div key='13'>
									<span className='me-2'>
										{t('impfschaeden.erfassen.weiteren_impfschaden_erfassen')}
									</span>
									<LoadingButton
										loading={loading}
										onClick={firstPage}
										variant='contained'
										color='success'
									>
										{t('impfschaeden.erfassen.zum_start')}
									</LoadingButton>
								</div>
							)}
						</Col>
						<Col
							xs={4}
							style={{
								flexDirection: 'row-reverse',
								justifyContent: 'end',
								display: 'flex',
							}}
						>
							<Stack direction='horizontal' gap={3} spacing={2}>
								{currentPage !== 1 && currentPage !== 13 && (
									<Button variant='light' onClick={prevPage} disabled={loading}>
										{t('common.zurueck')}
									</Button>
								)}
								{currentPage !== 13 &&
									currentPage !== 3 &&
									currentPage !== 12 && (
										<Button type='submit' onClick={nextPage}>
											{t('common.weiter')}
										</Button>
									)}
								{currentPage == 3 && (
									<Button className='invisible'>{t('common.Weiter')}</Button>
								)}
								{currentPage == 12 &&
									(user?.is_expert == 1 ||
										user?.is_admin == 1 ||
										user?.is_moderator == 1) && (
										<CheckBox
											label={t('impfschaden.erfassen.wiedervorlage')}
											className='text-nowrap'
											onChange={toggleKeepData}
											checked={keepData}
										/>
									)}

								{currentPage == 12 && (
									<LoadingButton
										key='send'
										loading={loading}
										onClick={() => saveVaxForm(false)}
										variant='contained'
									>
										{t('common.absenden')}
									</LoadingButton>
								)}

								{currentPage == 12 &&
									user?.is_expert == 1 &&
									!hasNewVaccineTypeOrManufacturer() && (
										<LoadingButton
											key='publish'
											loading={loading}
											onClick={() => saveVaxForm(true)}
											variant='contained'
											color='success'
										>
											{t('nutzerbereich.eintraege.bearbeiten.veroeffentlichen')}
										</LoadingButton>
									)}
							</Stack>
						</Col>
					</Row>
				</Container>
			</AssistantCard>
			{/* For development only. Remove before site go to live. */}
			{/* Page: {currentPage} */}
		</>
	);
};

ImpfFormStart.propTypes = {
	edit: PropTypes.bool,
};

export default ImpfFormStart;
